import React, { useMemo, useState } from 'react'
import {
    Card,
    Input,
    Button,
    Descriptions,
    Collapse,
    Tag,
    Typography,
    Space,
    Empty,
    message
} from 'antd'
import { FileSearchOutlined, DownloadOutlined } from '@ant-design/icons'
import axios from 'axios'
import moment from 'moment'
import { pd } from 'pretty-data'
import { getAuth } from '@misc'

const { Text, Title } = Typography
const { Panel } = Collapse

const API = process.env.REACT_APP_API_URL

// row columns that are booking metadata, not log payloads
const META_KEYS = [
    'id',
    'request_id',
    'reference',
    'client_ref',
    'total_price',
    'currency',
    'status',
    'user_id',
    'supplier_code',
    'created_at',
    'num_of_rooms',
    'hotel_id',
    'logs',
    'trace_id',
    'time',
    'user-id'
]

const WIRE_RE = /^(.+)_([A-Z0-9]+)_(\d{12,})_(request|response)$/

function classify(key, value) {
    const wire = key.match(WIRE_RE)
    if (wire) {
        return {
            group: 'wire',
            op: wire[1],
            supplier: wire[2],
            ts: Number(wire[3]),
            dir: wire[4]
        }
    }
    return { group: 'flow' }
}

function contentType(value) {
    if (typeof value !== 'string') return 'json'
    const v = value.trim()
    if (v.startsWith('<?xml') || v.startsWith('<soap') || v.startsWith('<')) return 'xml'
    if (v.startsWith('{') || v.startsWith('[')) return 'json-string'
    return 'text'
}

const looksXml = s =>
    typeof s === 'string' && /^\s*<(\?xml|soap|[A-Za-z])/.test(s) && s.includes('>')

// pull XML / JSON-string / long-text fields buried inside a JSON payload out
// into their own pretty blocks (e.g. a SOAP body inside supplier-prebook-*)
function extractEmbedded(value, path = '', out = []) {
    if (!value || typeof value !== 'object' || out.length >= 8) return out
    Object.entries(value).forEach(([k, v]) => {
        const p = path ? `${path}.${k}` : k
        if (typeof v === 'string' && v.length > 160) {
            if (looksXml(v)) {
                try {
                    out.push({ field: p, type: 'xml', text: pd.xml(v) })
                } catch (e) {
                    out.push({ field: p, type: 'text', text: v })
                }
            } else if (/^\s*[{[]/.test(v)) {
                try {
                    out.push({
                        field: p,
                        type: 'json',
                        text: JSON.stringify(JSON.parse(v), null, 2)
                    })
                } catch (e) {
                    /* not json, leave inline */
                }
            }
        } else if (v && typeof v === 'object') {
            extractEmbedded(v, p, out)
        }
    })
    return out
}

function render(value) {
    const type = contentType(value)
    if (type === 'json') {
        return {
            type,
            text: JSON.stringify(value, null, 2),
            embedded: extractEmbedded(value)
        }
    }
    if (type === 'xml') {
        try {
            return { type, text: pd.xml(value) }
        } catch (e) {
            return { type: 'text', text: value }
        }
    }
    if (type === 'json-string') {
        try {
            const parsed = JSON.parse(value)
            return {
                type: 'json',
                text: JSON.stringify(parsed, null, 2),
                embedded: extractEmbedded(parsed)
            }
        } catch (e) {
            return { type: 'text', text: value }
        }
    }
    // plain text: normalise escaped newlines/tabs and pipe-separated tokens
    let text = String(value)
    if (text.includes('\\n')) text = text.replace(/\\n/g, '\n').replace(/\\t/g, '  ')
    return { type, text }
}

const TYPE_COLORS = { json: 'geekblue', xml: 'purple', text: 'default' }

const PRE_STYLE = {
    maxHeight: 420,
    overflow: 'auto',
    background: '#fafafa',
    border: '1px solid #eee',
    borderRadius: 6,
    padding: 10,
    fontSize: 12,
    lineHeight: 1.5,
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
}

function LogPanelContent({ value }) {
    const { type, text, embedded } = useMemo(() => render(value), [value])
    const download = () => {
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `log.${type === 'xml' ? 'xml' : type === 'json' ? 'json' : 'txt'}`
        a.click()
        URL.revokeObjectURL(a.href)
    }
    return (
        <div>
            <Space style={{ marginBottom: 6 }}>
                <Typography.Paragraph copyable={{ text }} style={{ margin: 0 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Copy
                    </Text>
                </Typography.Paragraph>
                <Button size="small" type="text" icon={<DownloadOutlined />} onClick={download} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {text.length.toLocaleString()} chars
                </Text>
            </Space>
            <pre style={PRE_STYLE}>{text}</pre>
            {embedded && embedded.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    {embedded.map(e => (
                        <div key={e.field} style={{ marginTop: 8 }}>
                            <Space size={6} style={{ marginBottom: 4 }}>
                                <Tag color={TYPE_COLORS[e.type] || 'default'}>{e.type}</Tag>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    embedded in field
                                </Text>
                                <Text code>{e.field}</Text>
                                <Typography.Paragraph
                                    copyable={{ text: e.text }}
                                    style={{ margin: 0 }}
                                />
                            </Space>
                            <pre style={{ ...PRE_STYLE, maxHeight: 300 }}>{e.text}</pre>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function panelHeader(key, meta, value) {
    const type = contentType(value)
    if (meta.group === 'wire') {
        return (
            <Space size={6}>
                <Tag color={meta.dir === 'request' ? 'blue' : 'green'}>
                    {meta.dir.toUpperCase()}
                </Tag>
                <Tag>{meta.supplier}</Tag>
                <Text strong>{meta.op}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {moment(meta.ts).format('YYYY-MM-DD HH:mm:ss.SSS')}
                </Text>
                <Tag color={TYPE_COLORS[type] || 'default'}>{type.replace('-string', '')}</Tag>
            </Space>
        )
    }
    return (
        <Space size={6}>
            <Text strong>{key}</Text>
            <Tag color={TYPE_COLORS[type] || 'default'}>{type.replace('-string', '')}</Tag>
        </Space>
    )
}

export default function BookingLogs() {
    const [ref, setRef] = useState('')
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState(null)

    const load = async () => {
        const r = ref.trim()
        if (!r) return message.warning('Enter a booking ref (e.g. MHH005842)')
        setLoading(true)
        setData(null)
        try {
            const res = await axios({
                url: `${API}/hotel/booking-logs`,
                method: 'GET',
                params: { ref: r },
                headers: { 'Content-Type': 'application/json', ...getAuth() }
            })
            setData(res.data.logs || res.data)
        } catch (e) {
            const msg =
                (e.response && e.response.data && e.response.data.message) || e.message
            message.error(`Load logs failed: ${msg}`, 10)
        } finally {
            setLoading(false)
        }
    }

    const entries = useMemo(() => {
        if (!data) return { flow: [], wire: [] }
        const flow = []
        const wire = []
        Object.entries(data).forEach(([key, value]) => {
            if (META_KEYS.includes(key) || value == null) return
            const meta = classify(key, value)
            if (meta.group === 'wire') wire.push({ key, value, meta })
            else if (typeof value === 'object' || typeof value === 'string') {
                flow.push({ key, value, meta })
            }
        })
        wire.sort((a, b) => a.meta.ts - b.meta.ts)
        const ORDER = [
            'search-request',
            'prebook-request',
            'prebook-response',
            'supplier-prebook-request',
            'supplier-prebook-response',
            'book_req',
            'book_res'
        ]
        flow.sort((a, b) => {
            const ia = ORDER.indexOf(a.key)
            const ib = ORDER.indexOf(b.key)
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
        })
        return { flow, wire }
    }, [data])

    return (
        <div style={{ padding: 16 }}>
            <Title level={4}>Hotel Booking Logs</Title>
            <Text type="secondary">
                Full log trail of one booking — search/prebook/book payloads and the raw supplier
                request/response wire logs, pretty-printed.
            </Text>

            <Card size="small" style={{ marginTop: 16 }}>
                <Space.Compact style={{ width: 480, display: 'flex' }}>
                    <Input
                        placeholder="Booking ref — client_ref (MHH005842) "
                        value={ref}
                        onChange={e => setRef(e.target.value)}
                        onPressEnter={load}
                        allowClear
                    />
                    <Button
                        type="primary"
                        icon={<FileSearchOutlined />}
                        loading={loading}
                        onClick={load}
                    >
                        Load logs
                    </Button>
                </Space.Compact>
            </Card>

            {data && (
                <>
                    <Card size="small" style={{ marginTop: 16 }} title="Booking">
                        <Descriptions size="small" column={4} bordered>
                            <Descriptions.Item label="Client ref">
                                <Text strong copyable>
                                    {data.client_ref}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Reference">
                                <Text code copyable>
                                    {data.reference}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag
                                    color={
                                        data.status === 'Confirmed'
                                            ? 'green'
                                            : data.status === 'Failed'
                                              ? 'red'
                                              : 'orange'
                                    }
                                >
                                    {data.status}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Supplier">
                                <Tag>{data.supplier_code}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total">
                                <Text strong>
                                    {data.total_price} {data.currency}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Hotel id">
                                {data.hotel_id || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Created">
                                {data.created_at
                                    ? moment(data.created_at).format('YYYY-MM-DD HH:mm:ss')
                                    : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Request id">
                                <Text code copyable>
                                    {data.request_id}
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card
                        size="small"
                        style={{ marginTop: 16 }}
                        title={`Flow payloads (${entries.flow.length})`}
                    >
                        {entries.flow.length ? (
                            <Collapse>
                                {entries.flow.map(({ key, value, meta }) => (
                                    <Panel key={key} header={panelHeader(key, meta, value)}>
                                        <LogPanelContent value={value} />
                                    </Panel>
                                ))}
                            </Collapse>
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </Card>

                    <Card
                        size="small"
                        style={{ marginTop: 16 }}
                        title={`Supplier wire logs (${entries.wire.length}) — chronological`}
                    >
                        {entries.wire.length ? (
                            <Collapse>
                                {entries.wire.map(({ key, value, meta }) => (
                                    <Panel key={key} header={panelHeader(key, meta, value)}>
                                        <LogPanelContent value={value} />
                                    </Panel>
                                ))}
                            </Collapse>
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </Card>
                </>
            )}
        </div>
    )
}
